import React, { useEffect, useState } from 'react';
import { Button, Input, Select, SelectItem } from '@nextui-org/react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { classNames, copy } from '../../../utils';
import { SchoolService } from '../../../services'
import { useDispatch, useSelector } from 'react-redux';
import * as moment from 'moment';
import toast from 'react-hot-toast';

const ScheduleStep = ({ item }) => {
  const { form, onChange, addSchedule, removeSchedule, schedules, sections, subjects } = useForm(item);

  return (
    <div className="flex flex-col flex-1 gap-4">
      {item ? (
        <>
          <p>Edite el horario de la seccion seleccionada</p>
        </>
      ) : (
        <>
          <p>Seleccione la sección y proceda a señalar el horario de cada materia.</p>
          <p>Puede repetir para cada sección</p>
        </>
      )}

      <div className="flex gap-4">
        {sections.map(section => (
          <Button
            className={classNames(+form.section === section.id ? 'bg-green text-white' : '')}
            variant={form.section === section.id ? 'flat' : 'bordered'}
            radius="sm"
            onClick={() => onChange(section.id, 'section')}
          >
            {section.name}
          </Button>
        ))}
      </div>

      <div>
        <Button
          startContent={<IconPlus className="text-primary" />}
          onClick={addSchedule}
          variant="bordered"
        >
          Añadir fila
        </Button>
      </div>

      <div>
        <div
          className="grid items-center text-xs font-bold bg-default-100 text-foreground-500 py-2 px-1 rounded-xl [&>div]:p-1"
          style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 2rem' }}
        >
          <div>Hora de inicio</div>
          <div>Hora de fin</div>
          <div>Lunes</div>
          <div>Martes</div>
          <div>Miércoles</div>
          <div>Jueves</div>
          <div>Viernes</div>
          <div align="end"></div>
        </div>

        {(schedules[form.section] || []).map((subject, index) => (
          <ScheduleRow key={index} subject={subject} index={index} onChange={onChange} removeSchedule={removeSchedule} subjects={subjects} />
        ))}

        {!(schedules[form.section] || []).length && (
          <div className="w-full flex justify-center items-center p-4 text-foreground-500">
            <p>No hay horarios</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ScheduleRow = React.memo(({ subject, index, onChange, removeSchedule, subjects }) => {
  return (
    <div className="grid items-center [&>div]:p-1" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 2rem' }}>
      <div>
        <Input className="max-w-[130px]" type='time' radius="sm" variant="bordered" value={subject.from} onValueChange={v => onChange(v, 'from', index)} />
      </div>
      <div>
        <Input className="max-w-[130px]" type='time' radius="sm" variant="bordered" value={subject.to} onValueChange={v => onChange(v, 'to', index)} />
      </div>
      {['lunes', 'martes', 'miercoles', 'jueves', 'viernes'].map(day => (
        <div key={day}>
          <Select
            variant="bordered"
            radius="sm"
            className="max-w-[130px]"
            disallowEmptySelection={true}
            selectedKeys={subject[day] ? [String(subject[day])] : []} // Asegura que sea un array de strings
            onSelectionChange={v => onChange(v.currentKey, day, index)}
            aria-label="day"
          >
            <SelectItem key="null" value="null" title="Libre">
              Libre
            </SelectItem>
            {subjects.map((item) => (
              <SelectItem className='capitalize' key={item.id} value={String(item.id)} title={item.name}>
                {item.name}
              </SelectItem>
            ))}
          </Select>
        </div>
      ))}
      <div>
        <IconTrash className="text-danger cursor-pointer" onClick={() => removeSchedule(index)} />
      </div>
    </div>
  );
});



const updateSchedules = (sectionId, schedules) => ({
  type: 'UPDATE_SCHEDULES',
  payload: { sectionId, schedules }
});

const useForm = (item) => {
  const initialForm = {
    schoolYear: '',
    grade: '',
    section: '',
    schedules: {},
  };

  const emptySchedule = {
    from: '', to: '', lunes: null, martes: null, miercoles: null, jueves: null, viernes: null
  };

  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const scheduler = useSelector(state => state.scheduler);
  const [form, setForm] = useState(initialForm);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const validateScheduleTimes = (updatedSchedule, prevSchedule) => {
    // Validación de la hora de inicio y fin de la misma fila
    if (updatedSchedule.from && updatedSchedule.to) {
      const fromMoment = moment(updatedSchedule.from, 'HH:mm');
      const toMoment = moment(updatedSchedule.to, 'HH:mm');

      if (fromMoment.isAfter(toMoment)) {
        toast.error("La hora de inicio no puede ser mayor que la hora de fin.");
        return false;
      }
    }

    // Validación entre la fila actual y la fila anterior
    if (prevSchedule) {
      const previousToMoment = moment(prevSchedule.to, 'HH:mm');
      const currentFromMoment = moment(updatedSchedule.from, 'HH:mm');

      if (previousToMoment.isAfter(currentFromMoment)) {
        toast.error("La hora de fin de la fila anterior debe ser menor o igual que la hora de inicio de la fila actual.");
        return false;
      }
    }

    return true;
  };

  const onChange = (value, target, index) => {
    setForm(prevForm => {
      if (target === 'section') {
        return { ...prevForm, section: value };
      }

      const currentSection = prevForm.section;
      const newSchedules = prevForm.schedules[currentSection]?.map((schedule, i) => {
        if (i === index) {
          const updatedSchedule = { ...schedule, [target]: value };

          // Validar las horas usando la función auxiliar
          const prevSchedule = index > 0 ? prevForm.schedules[currentSection][index - 1] : null;

          if (!validateScheduleTimes(updatedSchedule, prevSchedule)) {
            return schedule;  // Si la validación falla, no se actualiza el estado
          }

          return updatedSchedule;
        }
        return schedule;
      }) || [];

      // Actualizar el estado global
      dispatch(updateSchedules(currentSection, newSchedules));

      return {
        ...prevForm,
        schedules: {
          ...prevForm.schedules,
          [currentSection]: newSchedules,
        },
      };
    });
  };

  const addSchedule = () => {
    setForm(prevForm => {
      const newSchedules = [
        ...(prevForm.schedules[prevForm.section] || []),
        emptySchedule,
      ];

      // Actualizar el estado global
      dispatch(updateSchedules(prevForm.section, newSchedules));

      return {
        ...prevForm,
        schedules: {
          ...prevForm.schedules,
          [prevForm.section]: newSchedules,
        },
      };
    });
  };

  const removeSchedule = (index) => {
    setForm(prevForm => {
      const newSchedules = prevForm.schedules[prevForm.section].filter((_, i) => i !== index);

      // Actualizar el estado global
      dispatch(updateSchedules(prevForm.section, newSchedules));

      return {
        ...prevForm,
        schedules: {
          ...prevForm.schedules,
          [prevForm.section]: newSchedules,
        },
      };
    });
  };


  const fetchSchedules = async () => {
    // Si viene el item significa que es un edit, si se muestra la sección
    if (item) return

    const { school_year_id, school_grade_id } = form;
    const res = await SchoolService.schoolAdmin.getSchedulers({
      school_id: user.id,
      school_grade_id: school_grade_id,
      school_year_id: school_year_id,
    });

    const { data } = res
    // Secciones ya seleccionadas de la base de datos
    const sections = data?.flatMap(x => x.sections)

    // Secciones que vienen del grado
    const currentSections = scheduler.sections


    // Solo mostrar las secciones que no estén ya asignadas
    setSections(currentSections.filter(x => !sections.find(y => y.id === x.id)))
  }


  const fetchSubjects = async (item) => {
    const res = await SchoolService.schoolAdmin.getSubjects({ school_id: user.id, school_grade_id: item.school_grade_id });
    const data = res.data.length > 0 ? res.data[0]?.subjects : [];
    setSubjects(data.map((x) => ({ ...x, isSelected: true })));
  }

  useEffect(() => {
    // Si viene el item significa que es un edit, cargamos los datos
    if (item) {
      setSections([{ id: item.id, name: item.name }])
      fetchSubjects(item).then(() => {
        const scheduleData = item.schedulers.map((x) => ({
          ...x,
          lunes: x.lunes ? String(x.lunes.id) : 'null',
          martes: x.martes ? String(x.martes.id) : 'null',
          miercoles: x.miercoles ? String(x.miercoles.id) : 'null',
          jueves: x.jueves ? String(x.jueves.id) : 'null',
          viernes: x.viernes ? String(x.viernes.id) : 'null',
        }))

        setForm({
          schoolYear: item.school_year_id,
          grade: item.school_grade_id,
          section: item.id,
          schedules: {
            [item.id]: scheduleData
          }
        })

        dispatch({
          type: 'ADD_DATA', payload: {
            school_section_id: item.id,
            school_grade_id: item.school_grade_id,
            school_year_id: item.school_year_id,
          }
        });
        dispatch(updateSchedules(item.id, scheduleData));
      })
      return
    }

    // Si no viene el item significa que es un nuevo, cargamos los datos del reducer
    setSections(scheduler?.sections ?? []);
    setSubjects(scheduler?.subjects ?? []);

  }, [item])

  useEffect(() => {
    if (sections.length < 1) return;
    setForm(s => ({ ...s, section: String(sections[0].id) }));
  }, [sections])

  useEffect(() => {
    fetchSchedules();
  }, [])



  return {
    form,
    onChange,
    addSchedule,
    removeSchedule,
    schedules: form.schedules,
    sections,
    subjects: subjects.filter(x => x.isSelected)
  };
};

export default ScheduleStep;
